import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Adds a calm, one-time entrance motion to content sections. The component
 * deliberately does nothing for users who prefer reduced motion.
 */
export const SiteMotion = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || !("IntersectionObserver" in window)) return undefined;

    let observer;
    let depthItems = [];
    const frame = window.requestAnimationFrame(() => {
      const sections = [...document.querySelectorAll("main section")].filter(
        (section) =>
          !section.matches('[data-testid="hero-section"], [data-testid="product-page-hero"], .kitchen-hero, .electronics-hero, [data-testid="galvalum-hero"]')
      );

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8%", threshold: 0.08 }
      );

      sections.forEach((section) => {
        section.classList.add("reveal-on-scroll");
        [...section.children].slice(0, 8).forEach((child, index) => {
          child.classList.add("reveal-child");
          child.style.setProperty("--reveal-delay", `${Math.min(index * 70, 280)}ms`);
        });
        observer.observe(section);
      });

      depthItems = [...document.querySelectorAll("main article")].filter((item) => item.querySelector("img"));
      depthItems.forEach((item) => {
        item.classList.add("depth-hover");
        item.querySelector("img")?.classList.add("depth-hover__media");
      });
    });

    const onPointerMove = (event) => {
      const item = event.target.closest?.(".depth-hover");
      if (!item || window.innerWidth < 900) return;
      const rect = item.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      item.style.setProperty("--depth-rotate-x", `${(y * -3).toFixed(2)}deg`);
      item.style.setProperty("--depth-rotate-y", `${(x * 3).toFixed(2)}deg`);
      item.style.setProperty("--depth-shift-x", `${(x * -7).toFixed(2)}px`);
      item.style.setProperty("--depth-shift-y", `${(y * -7).toFixed(2)}px`);
    };
    const onPointerOut = (event) => {
      const item = event.target.closest?.(".depth-hover");
      if (!item || item.contains(event.relatedTarget)) return;
      item.style.removeProperty("--depth-rotate-x");
      item.style.removeProperty("--depth-rotate-y");
      item.style.removeProperty("--depth-shift-x");
      item.style.removeProperty("--depth-shift-y");
    };
    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("pointerout", onPointerOut, { passive: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerout", onPointerOut);
      depthItems.forEach((item) => item.classList.remove("depth-hover"));
    };
  }, [pathname]);

  return null;
};
